import {
  DVIRCustomFieldBucket,
  DVIRCustomFieldValueEntity,
  DVIREntity,
  humanizeDefCode,
  matchesDVIRCustomFieldBucket
} from "@ots-frontend/ots-data";
import {ActionIcon, Alert, Button, Card, Divider, Grid, Group, Image, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {IconAlertCircle, IconAlertTriangleFilled, IconCircleCheckFilled, IconMessagePlus} from "@tabler/icons-react";
import {EntityCommentList} from "../../comment/EntityCommentList/EntityCommentList";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {FormattedDateTime, getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useNavigate} from "react-router-dom";
import {useCallback, useMemo} from "react";

const MechanicCard = (props: { dvir: DVIREntity }) => {
  const {dvir} = props

  const {t} = useTranslation(["common"])

  const navigate = useNavigate()

  const onReviewButtonClick = useCallback(() => {
    navigate(`/dvirs/mechanic-review`, { state: { id: dvir.id}})
  }, [navigate, dvir.id])

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>

      <Group position="apart" pb="sm">
        <Title order={4}>
          {t("common.entity.dvir.profile.info.section.mechanic.label", "Mechanic Review")}
        </Title>
      </Group>

      <Divider/>

      <Grid>
        <Grid.Col span={12}>
          {dvir.requiresMechanicReview ? (
            <>
              {dvir.isUnresolved &&
                <>
                  <Alert icon={<IconAlertCircle size="1rem"/>} title="Pending" color="warning" m="sm" radius="lg">
                    Pending a review from a mechanic.
                  </Alert>
                  <HasAccess permissions={["dvir_review"]}>
                    <Button onClick={onReviewButtonClick} fullWidth={true}>
                      {t("common.action.review", "Review")}
                    </Button>
                  </HasAccess>
                </>
              }
              {!dvir.isUnresolved &&
                <>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    Defects Corrected
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <Group spacing="xs">
                      {dvir.defectsCorrected ? "Yes" : "No"}
                    </Group>
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    Defects Need Not Corrected
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <Group spacing="xs">
                      {dvir.defectsNeedNotCorrected ? "Yes" : "No"}
                    </Group>
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    Remarks
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    {dvir.defectsComments}
                  </Text>
                  {(dvir.mechanicSignatureDttm && dvir.mechanicSignatureUrl) &&
                    <>
                      <Divider variant="dotted" my="md"/>
                      <Text ta="left" fz="sm" weight={500} mt="md">
                        Signed on {getFormattedDateTime(dvir.mechanicSignatureDttm)} by {(dvir.mechanicName ?? dvir.mechanicPersonName)}
                      </Text>
                      <Image
                        src={dvir.mechanicSignatureUrl}
                        alt="Mechanic Signature"
                        fit="contain" // preserve aspect ratio
                        styles={{
                          image: {
                            maxHeight: 150,   // limit height
                            height: "auto",   // scale proportionally
                            maxWidth: "100%", // prevent overflow horizontally
                          },
                        }}
                      />
                    </>
                  }
                  {(dvir.person2SignatureDttm && dvir.person2SignatureUrl) &&
                    <>
                      <Divider variant="dotted" my="md"/>
                      <Divider variant="dotted" my="md"/>
                      <Text ta="left" fz="sm" weight={500} mt="md">
                        Acknowledged on {getFormattedDateTime(dvir.person2SignatureDttm)} by {dvir.person2Name}
                      </Text>
                      <Image
                        src={dvir.person2SignatureUrl}
                        alt="Ack Signature"
                        fit="contain" // preserve aspect ratio
                        styles={{
                          image: {
                            maxHeight: 150,   // limit height
                            height: "auto",   // scale proportionally
                            maxWidth: "100%", // prevent overflow horizontally
                          },
                        }}
                      />
                    </>
                  }
                </>
              }
            </>
          ) : (
            <Alert icon={<IconCircleCheckFilled size="1rem"/>} title="Complete" color="green" m="sm" radius="lg">
              Review Not Needed
            </Alert>
          )}
        </Grid.Col>
      </Grid>
    </Card>
  )
}

export const DVIRVehicleInspectionContent = (props: { dvir: DVIREntity }) => {
  const {dvir} = props

  const {t} = useTranslation(["common"])

  const customFields = useMemo(() => {
    return (dvir.customFieldValues ?? [])
      .filter((v) => dvir.isCustomChecklistField(v))
      .filter((v) => matchesDVIRCustomFieldBucket(DVIRCustomFieldBucket.VEHICLE, v.customFieldDefCode));
  }, [dvir]);

  return (
    <>

      <Group position="apart" pb="sm">
        <Title order={4}>
          {t("common.entity.dvir.profile.info.section.vehicle.label", "Vehicle Inspection")}
        </Title>
        {dvir.vehicleItemDefectiveCount > 0 &&
          <Text ta="left" c="red" fz="xs">
            <Group spacing="xs">
              <IconAlertTriangleFilled size="14"/>
              {`${dvir.vehicleItemDefectiveCount} / ${dvir.vehicleItemCount} Defective Items`}
            </Group>
          </Text>
        }
      </Group>

      <Divider/>

      <Grid>
        {/* Base fields */}
        {DVIREntity.VEHICLE_INSPECTION_FIELDS.map((field) => {
          const value: boolean = dvir[field as keyof DVIREntity] as boolean
          return (
            <Grid.Col key={field} span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
              </Text>
              <Text ta="left" c={value ? "red" : "dimmed"} fz="xs">
                <Group spacing="xs">
                  {value && <IconAlertTriangleFilled size="14"/>}
                  {value ? "Defective" : "Operational"}
                </Group>
              </Text>
            </Grid.Col>
          )
        })}

        {/* custom fields */}
        {customFields.map((v) => {
          const label = v.label ?? humanizeDefCode(v.customFieldDefCode);
          const isDefective = v.booleanValue === true;

          return (
            <Grid.Col key={v.id ?? v.customFieldProfileItemId} span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {label}
              </Text>
              <Text ta="left" c={isDefective ? "red" : "dimmed"} fz="xs">
                <Group spacing="xs">
                  {isDefective && <IconAlertTriangleFilled size="14" />}
                  {isDefective ? "Defective" : "Operational"}
                </Group>
              </Text>
            </Grid.Col>
          );
        })}
      </Grid>
    </>
  )
}

export const DVIRVehicleInspectionCard = (props: { dvir: DVIREntity }) => {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <DVIRVehicleInspectionContent dvir={props.dvir}/>
    </Card>
  )
}

export const DVIRTrailerInspectionContent = (props: { dvir: DVIREntity }) => {
  const {dvir} = props

  const {t} = useTranslation(["common"])

  const customFields = useMemo(() => {
    return (dvir.customFieldValues ?? [])
      .filter((v) => dvir.isCustomChecklistField(v))
      .filter((v) => matchesDVIRCustomFieldBucket(DVIRCustomFieldBucket.TRAILER, v.customFieldDefCode));
  }, [dvir]);

  return (
    <>

      <Group position="apart" pb="sm">
        <Title order={4}>
          {t("common.entity.dvir.profile.info.section.trailer.label", "Trailer Inspection")}
        </Title>
        {dvir.trailerItemDefectiveCount > 0 &&
          <Text ta="left" c="red" fz="xs">
            <Group spacing="xs">
              <IconAlertTriangleFilled size="14"/>
              {`${dvir.trailerItemDefectiveCount} / ${dvir.trailerItemCount} Defective Items`}
            </Group>
          </Text>
        }
      </Group>

      <Divider/>

      <Grid>
        {/* Base fields */}
        {DVIREntity.TRAILER_INSPECTION_FIELDS.map((field) => {
          const value: boolean = dvir[field as keyof DVIREntity] as boolean
          return (
            <Grid.Col key={field} span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
              </Text>
              <Text ta="left" c={value ? "red" : "dimmed"} fz="xs">
                <Group spacing="xs">
                  {value && <IconAlertTriangleFilled size="14"/>}
                  {value ? "Defective" : "Operational"}
                </Group>
              </Text>
            </Grid.Col>
          )
        })}

        {/* custom fields */}
        {customFields.map((v) => {
          const label = v.label ?? humanizeDefCode(v.customFieldDefCode);
          const isDefective = v.booleanValue === true;

          return (
            <Grid.Col key={v.id ?? v.customFieldProfileItemId} span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {label}
              </Text>
              <Text ta="left" c={isDefective ? "red" : "dimmed"} fz="xs">
                <Group spacing="xs">
                  {isDefective && <IconAlertTriangleFilled size="14" />}
                  {isDefective ? "Defective" : "Operational"}
                </Group>
              </Text>
            </Grid.Col>
          );
        })}
      </Grid>
    </>
  )
}

export const DVIRTrailerInspectionCard = (props: { dvir: DVIREntity }) => {
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <DVIRTrailerInspectionContent dvir={props.dvir}/>
    </Card>
  )
}

const DVIRCustomInspectionContent = (props: {
  title: string;
  values: DVIRCustomFieldValueEntity[];
}) => {
  const { title, values } = props;

  const itemCount = values.length;
  const defectiveCount = values.filter((v) => v.booleanValue === true).length;

  if (itemCount === 0) return null;

  return (
    <>
      <Group position="apart" pb="sm">
        <Title order={4}>{title}</Title>

        {defectiveCount > 0 && (
          <Text ta="left" c="red" fz="xs">
            <Group spacing="xs">
              <IconAlertTriangleFilled size="14" />
              {`${defectiveCount} / ${itemCount} Defective Items`}
            </Group>
          </Text>
        )}
      </Group>

      <Divider />

      <Grid>
        {values.map((v) => {
          const label = v.label ?? humanizeDefCode(v.customFieldDefCode)
          const isDefective = v.booleanValue === true;

          return (
            <Grid.Col key={v.id ?? v.customFieldProfileItemId} span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {label}
              </Text>
              <Text ta="left" c={isDefective ? "red" : "dimmed"} fz="xs">
                <Group spacing="xs">
                  {isDefective && <IconAlertTriangleFilled size="14" />}
                  {isDefective ? "Defective" : "Operational"}
                </Group>
              </Text>
            </Grid.Col>
          );
        })}
      </Grid>
    </>
  );
};

const DVIRCustomInspectionCard = (props: {
  title: string;
  values: DVIRCustomFieldValueEntity[];
}) => {
  const { title, values } = props;

  if (values.length === 0) return null;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <DVIRCustomInspectionContent title={title} values={values} />
    </Card>
  );
};

export const DVIRDetailsContent = (props: { dvir: DVIREntity }) => {

  const {dvir} = props

  const {t} = useTranslation(["common"])

  return (
    <>

      <Group position="apart" pb="sm">
        <Title
          order={4}>{t("common.entity.dvir.profile.info.section.details.label", "Details")}</Title>
      </Group>

      <Divider/>

      <Grid>
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("common.entity.dvir.profile.info.field.date.label", "Date")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            <FormattedDateTime date={dvir.dttm}/>
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("common.entity.dvir.profile.info.field.person.label", "Driver")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            <EntityAnchor
              value={{
                objectId: dvir.personId,
                objectName: dvir.personName,
                objectType: "PERSON"
              }}/>
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("common.entity.dvir.profile.info.field.vehicle.label", "Vehicle")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {dvir.vehicleId &&
              <EntityAnchor
                value={{
                  objectId: dvir.vehicleId,
                  objectName: dvir.vehicleName,
                  objectType: "VEHICLE"
                }}/>
            }
          </Text>
        </Grid.Col>
        <Grid.Col span={6}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            {t("common.entity.dvir.profile.info.field.trailer.label", "Trailer")}
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {dvir.trailerId &&
              <EntityAnchor
                value={{
                  objectId: dvir.trailerId,
                  objectName: dvir.trailerName,
                  objectType: "TRAILER"
                }}/>
            }
          </Text>
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            Remarks
          </Text>
          <Text ta="left" c="dimmed" fz="xs">
            {dvir.comments}
          </Text>
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider variant="dotted"/>
        </Grid.Col>
        <Grid.Col span={12}>
          <Text ta="left" fz="sm" weight={500} mt="md">
            Review
          </Text>
          <Text ta="left" c={dvir.satisfactory ? "green" : "red"} fz="xs">
            <Group spacing="xs">
              {dvir.satisfactory ? <IconCircleCheckFilled size="14"/> : <IconAlertTriangleFilled size="14"/>}
              {dvir.satisfactory ? "Satisfactory" : "Unsatisfactory"}
              {dvir.defectiveItemCount > 0 &&
                <Text ta="left" c="red" fz="xs">
                  <Group spacing="xs">
                    {`(${dvir.defectiveItemCount} / ${dvir.itemCount} Defective Items)`}
                  </Group>
                </Text>
              }
            </Group>
          </Text>
          {(dvir.personSignatureDttm && dvir.personSignatureUrl) &&
            <>
              <Divider variant="dotted" my="md"/>
              <Text ta="left" fz="sm" weight={500} mt="md">
                Signed on {getFormattedDateTime(dvir.personSignatureDttm)} by {dvir.personName}
              </Text>
              <Image
                src={dvir.personSignatureUrl}
                alt="Driver Signature"
                fit="contain" // preserve aspect ratio
                styles={{
                  image: {
                    maxHeight: 150,   // limit height
                    height: "auto",   // scale proportionally
                    maxWidth: "100%", // prevent overflow horizontally
                  },
                }}
              />
            </>
          }
        </Grid.Col>
      </Grid>
    </>
  )
}

export const DVIRDetailsCard = (props: { dvir: DVIREntity }) => {
  const {dvir} = props
  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <DVIRDetailsContent dvir={dvir}/>
    </Card>
  )
}

export type DVIRDetailsProps = {
  dvir: DVIREntity
}

export const DVIRDetails = (props: DVIRDetailsProps) => {
  const {dvir} = props;

  const {t} = useTranslation(["common"]);

  const unknownCustomFields = useMemo(() => {
    return (dvir.customFieldValues ?? [])
      .filter((v) => dvir.isCustomChecklistField(v))
      .filter((v) => matchesDVIRCustomFieldBucket(DVIRCustomFieldBucket.UNKNOWN, v.customFieldDefCode));
  }, [dvir]);

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: dvir
  });

  return (
    <Grid>

      <Grid.Col span={6}>
        <DVIRDetailsCard dvir={dvir}/>
      </Grid.Col>

      <Grid.Col span={6}>
        <MechanicCard dvir={dvir}/>
      </Grid.Col>

      <Grid.Col span={6}>
        <DVIRVehicleInspectionCard dvir={dvir}/>
      </Grid.Col>

      <Grid.Col span={6}>
        <DVIRTrailerInspectionCard dvir={dvir}/>
      </Grid.Col>

      {unknownCustomFields.length > 0 && (
        <Grid.Col span={12}>
          <DVIRCustomInspectionCard
            title={t(
              "common.entity.dvir.profile.info.section.custom.label",
              "Custom Inspection Items"
            )}
            values={unknownCustomFields}
          />
        </Grid.Col>
      )}

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.dvir.profile.info.section.comments.label", "Comments")}</Title>
            <HasAccess permissions={["dispatch_edit"]}>
              <ActionIcon color="green" onClick={handlers.open}>
                <IconMessagePlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <EntityCommentList objectId={dvir.id}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>

    </Grid>
  );
};

